import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ADDENDUM_STATUSES,
  isReporterLockedState,
  ReportStatus,
  reportStatusState,
} from '../../state';
import { useCurrentCaseClaimedStatus } from 'hooks/useCurrentCaseClaimedStatus';
import { useEffect } from 'react';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { WithApolloContext } from 'modules/Apollo/types';
import type { LoadedReportWorklistItem, Report } from 'hooks/useCurrentCaseReport';

export type UseReportLockedStateReturn = {
  locked: boolean;
  isClaimedByMe: boolean;
  loading: boolean;
  claimedBy: LoadedReportWorklistItem['claimedBy'];
  submittedBy: Report['owner'];
  isAddendumStatus: boolean;
  isSubmittedStatus: boolean;
};

export const useReportLockedState = ({
  context,
}: WithApolloContext): UseReportLockedStateReturn => {
  const { currentCase: worklistItem, loadingCase } = useCurrentCaseContext();

  const { isClaimedByMe, isClaimedBySomeoneElse, isClaimed, isArchived } =
    useCurrentCaseClaimedStatus({ context });

  const status = useRecoilValue(reportStatusState);
  const setIsReporterLocked = useSetRecoilState(isReporterLockedState);
  const isAddendumStatus = ADDENDUM_STATUSES.includes(status as (typeof ADDENDUM_STATUSES)[number]);

  const isSubmittedStatus =
    status === ReportStatus.ProvisionalSubmit || status === ReportStatus.Submitted;

  const unclaimed = !isClaimed && !isArchived;

  const locked =
    loadingCase ||
    worklistItem == null ||
    isClaimedBySomeoneElse ||
    unclaimed ||
    ReportStatus.Submitted === status;

  useEffect(() => {
    setIsReporterLocked(locked);
  }, [locked, setIsReporterLocked]);

  return {
    locked,
    isClaimedByMe,
    loading: loadingCase,
    claimedBy: worklistItem?.claimedBy,
    submittedBy: worklistItem?.report?.owner,
    isAddendumStatus,
    isSubmittedStatus,
  };
};
